import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';

import { FilterOptionNotFoundBanner } from '@/components/reports/FilterOptionNotFoundBanner';
import { Separator } from '@/components/ui/separator';
import { QuickSightDashboard } from '@/features/quicksight/components/QuickSightDashboard';
import { QuickSightEmbedError } from '@/features/quicksight/components/QuickSightEmbedError';
import { useQuickSightEmbedUrl } from '@/features/quicksight/hooks/useQuickSightEmbedUrl';
import { AppPageContent } from '@/features/shared/components/AppPageContent';
import { AppPageHeader } from '@/features/shared/components/AppPageHeader';
import { useOneRosterAuth } from '@/hooks/useOneRosterAuth';
import { UserRoles } from '@/lib/constants/auth.constants.ts';
import { getLocalTimezoneOffset } from '@/lib/datetime';
import { appendHashParams, resolveDashboardId } from '@/lib/quicksight';
import { requireRole } from '@/lib/validation';

export const Route = createFileRoute('/_app/app/student-xp-report')({
  component: RouteComponent,
  beforeLoad: requireRole([UserRoles.ADMINISTRATOR, UserRoles.AIDE]),
});

function RouteComponent() {
  const { isAdmin, user, isSysAdmin } = useOneRosterAuth();
  const isAdminUser = isAdmin || isSysAdmin;

  const dashboardId = resolveDashboardId('studentXp');
  const {
    data,
    isLoading: isEmbedLoading,
    isError: isEmbedError,
  } = useQuickSightEmbedUrl(dashboardId);

  const { offsetHours, offsetHourStr } = useMemo(() => {
    return getLocalTimezoneOffset();
  }, []);

  const quickSightSrc = useMemo(() => {
    if (!data?.embedUrl) return '';

    const hashParams: Record<string, string | undefined> = {};

    // Only add aideId parameter if user is an aide (not admin or sysAdmin)
    if (!isAdminUser && user?.sourcedId) {
      hashParams['p.aideId'] = user.sourcedId;
    }

    // Add timezone parameters
    hashParams['p.tzHourOffset'] = String(offsetHours);
    hashParams['p.offset'] = offsetHourStr;

    return appendHashParams(data.embedUrl, hashParams);
  }, [data?.embedUrl, isAdminUser, user?.sourcedId, offsetHours, offsetHourStr]);

  return (
    <AppPageContent>
      <AppPageHeader
        title="Student XP Report"
        breadcrumbItems={[
          {
            label: 'App',
            link: { to: '/app' },
          },
          {
            label: 'Student XP Report',
            link: { to: '/app/student-xp-report' },
          },
        ]}
      />
      <div className="p-4 w-full max-w-full">
        <FilterOptionNotFoundBanner />

        {isEmbedError && <QuickSightEmbedError className="mb-4" />}

        <Separator className="my-4" />
        <QuickSightDashboard
          src={quickSightSrc}
          isEmbedLoading={isEmbedLoading}
          iframeKey={`${user?.sourcedId}-${isAdminUser}-${offsetHours}`}
          className="border rounded-md shadow-sm"
        />
      </div>
    </AppPageContent>
  );
}
